// Pure helpers for the local Storage backup: Windows-safe paths and "what needs downloading".

export const LOCAL_MANIFEST_FILE = '_guidesofia-backup-manifest.json';

// eslint-disable-next-line no-control-regex -- control characters are invalid in Windows names
const INVALID_WINDOWS_CHARS = /[<>:"\\|?*\u0000-\u001f]/g;
const RESERVED_WINDOWS_NAMES = /^(con|prn|aux|nul|com[1-9]|lpt[1-9])(\..*)?$/i;

const shortHash = (text) => {
  let hash = 5381;
  for (let i = 0; i < text.length; i += 1) {
    hash = ((hash << 5) + hash + text.charCodeAt(i)) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
};

const toSafeSegment = (segment) => {
  let safe = segment.replace(INVALID_WINDOWS_CHARS, '_').replace(/[. ]+$/, '');
  if (!safe || safe === '.' || safe === '..') safe = '_';
  if (RESERVED_WINDOWS_NAMES.test(safe)) safe = `_${safe}`;
  return safe;
};

// Chrome cannot write a file whose full Windows path passes 260 characters, and the backup folder
// itself uses part of that (e.g. "I:\My Drive\...\firebase_backup_auto\" is ~70), so paths inside it stay shorter.
export const MAX_LOCAL_PATH_LENGTH = 160;
const HASH_SUFFIX_LENGTH = 9;
const MIN_SEGMENT_LENGTH = 16;

const splitExtension = (segment) => {
  const dot = segment.lastIndexOf('.');
  return dot > 0 ? [segment.slice(0, dot), segment.slice(dot)] : [segment, ''];
};

// Trims the file name first, then the longest folder names, until the path fits
const shortenToFit = (segments) => {
  const shortened = [...segments];
  const last = shortened.length - 1;
  const excess = () => shortened.join('/').length + HASH_SUFFIX_LENGTH - MAX_LOCAL_PATH_LENGTH;

  const [stem, extension] = splitExtension(shortened[last]);
  const keep = Math.max(MIN_SEGMENT_LENGTH, stem.length - Math.max(0, excess()));
  shortened[last] = `${stem.slice(0, keep)}${extension}`;

  while (excess() > 0) {
    let longest = -1;
    for (let i = 0; i < last; i += 1) {
      if (shortened[i].length > MIN_SEGMENT_LENGTH && (longest === -1 || shortened[i].length > shortened[longest].length)) longest = i;
    }
    if (longest === -1) break;
    const length = Math.max(MIN_SEGMENT_LENGTH, shortened[longest].length - excess());
    shortened[longest] = toSafeSegment(shortened[longest].slice(0, length));
  }
  return shortened;
};

// Storage names that are already valid and short enough keep their exact path. Names that had to
// change get a short hash of the original name, so two different objects can never land on the same file.
export const toLocalPath = (objectName) => {
  const segments = String(objectName).split('/').filter(Boolean);
  let safeSegments = segments.map(toSafeSegment);
  if (safeSegments.join('/').length > MAX_LOCAL_PATH_LENGTH) safeSegments = shortenToFit(safeSegments);
  const changed = safeSegments.some((segment, index) => segment !== segments[index]);
  if (!changed) return safeSegments.join('/');

  const last = safeSegments.length - 1;
  const dot = safeSegments[last].lastIndexOf('.');
  const suffix = `~${shortHash(objectName)}`;
  safeSegments[last] = dot > 0
    ? `${safeSegments[last].slice(0, dot)}${suffix}${safeSegments[last].slice(dot)}`
    : `${safeSegments[last]}${suffix}`;
  return safeSegments.join('/');
};

export const isObjectOutdated = (remote, local) => {
  if (!local) return true;
  if (remote.m && local.m) return remote.m !== local.m;
  return remote.s !== local.s || remote.u !== local.u;
};

export const buildBackupStatus = ({ remoteObjects, localFiles = {} }) => {
  const outdated = [];
  let totalBytes = 0;
  let outdatedBytes = 0;
  const remoteNames = new Set();

  for (const remote of remoteObjects) {
    remoteNames.add(remote.n);
    totalBytes += remote.s;
    if (isObjectOutdated(remote, localFiles[remote.n])) {
      outdated.push(remote);
      outdatedBytes += remote.s;
    }
  }

  const localOnly = Object.keys(localFiles).filter((name) => !remoteNames.has(name)).length;

  return {
    totalFiles: remoteObjects.length,
    totalBytes,
    upToDate: remoteObjects.length - outdated.length,
    outdated,
    outdatedBytes,
    localOnly
  };
};

export const formatBytes = (bytes) => {
  if (!bytes) return '0 MB';
  if (bytes >= 1073741824) return `${(bytes / 1073741824).toFixed(1)} GB`;
  return `${Math.max(0.1, bytes / 1048576).toFixed(1)} MB`;
};
